// components/PrivateRoute.jsx
import { Navigate } from "react-router";

/**
 * A wrapper component for protecting routes based on authentication status and user roles.
 *
 * @param {object} props - The component's props.
 * @param {React.ReactNode} props.children - The child components (the protected route content) to render if access is allowed.
 * @param {boolean} props.isLoggedIn - The authentication status of the user.
 * @param {string} props.role - The role of the currently logged-in user (e.g., 'admin', 'organizer').
 * @param {string[]} [props.allowedRoles] - An optional array of roles that are allowed to access this route.
 * @returns {React.ReactElement} Either the `children` (if authorized) or a `<Navigate>` component to redirect to the login page.
 */
const PrivateRoute = ({ children, isLoggedIn, role, allowedRoles }) => {
    // 1. Check if the user is logged in
    if (!isLoggedIn) {
        return <Navigate to="/admin/login" />;
    }

    // 2. Check if the user's role is in allowedRoles (if specified)
    if (allowedRoles && !allowedRoles.includes(role)) {
        // Redirect if the role is not permitted
        return <Navigate to="/admin/login" />
    }

    // 3. If logged in and role is permitted (or no role restriction), render the children
    return children;
};

export default PrivateRoute;